import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Logo } from '../components/Logo';
import { BookIcon, SpeakerIcon } from '../components/Icons';
import { strings } from '../app/strings';
import { readSave, useGameStore, type LocalMode } from '../store/gameStore';
import { useSettingsStore } from '../store/settingsStore';

export function Home() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const muted = useSettingsStore((state) => state.muted);
  const toggleMuted = useSettingsStore((state) => state.toggleMuted);
  const latestSave = useMemo(() => {
    const saves = [readSave('local'), readSave('ai')].filter((save): save is NonNullable<typeof save> => save !== null && save.state.phase !== 'gameOver');
    return saves.sort((a, b) => b.savedAt - a.savedAt)[0] ?? null;
  }, []);
  const resume = () => {
    if (!latestSave) return;
    useGameStore.getState().restore(latestSave.mode);
    navigate(`/play/${latestSave.mode}`);
  };
  const join = () => {
    const normalized = code.trim().toUpperCase();
    if (/^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/.test(normalized)) navigate(`/join/${normalized}`);
  };
  return (
    <main className="home-page page-shell">
      <header className="hero"><Logo /><p>{strings.tagline}</p></header>
      {latestSave && <button className="resume-card" onClick={resume}><span>▶</span><div><strong>Resume game</strong><small>{latestSave.mode === 'ai' ? `vs ${latestSave.config.names[1]}` : `${latestSave.config.names[0]} & ${latestSave.config.names[1]}`}</small></div></button>}
      <section className="mode-grid" aria-label="Choose game mode">
        <ModeCard mode="local" icon="🤝" />
        <ModeCard mode="online" icon="🌐" />
        <ModeCard mode="ai" icon="🤖" />
      </section>
      <Card className="room-joiner"><label htmlFor="room-code">Have a room code?</label><div><input id="room-code" value={code} maxLength={6} placeholder="ABC234" onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ''))} onKeyDown={(event) => { if (event.key === 'Enter') join(); }} /><Button onClick={join} disabled={!/^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/.test(code)}>Join</Button></div></Card>
      <Link className="rules-link" to="/rules"><BookIcon /> How to play</Link>
      <footer><p>Chance &amp; Change is a fan-made digital adaptation of Coin Age.</p><button className="icon-button" onClick={toggleMuted} aria-label={muted ? 'Turn sound on' : 'Mute sound'}><SpeakerIcon off={muted} /></button></footer>
    </main>
  );
}

function ModeCard({ mode, icon }: { mode: 'local' | 'online' | 'ai'; icon: string }) {
  return <Link to={`/setup/${mode}`} className="mode-card card"><span className="mode-card__icon">{icon}</span><h2>{strings.modes[mode].title}</h2><p>{strings.modes[mode].blurb}</p></Link>;
}

export function latestSaveMode(): LocalMode | null {
  const saves = [readSave('local'), readSave('ai')].filter((save): save is NonNullable<typeof save> => save !== null);
  return saves.sort((a, b) => b.savedAt - a.savedAt)[0]?.mode ?? null;
}
